import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  useUpdateCommunitySettings,
  useTransferOwnership,
} from '../../hooks/use-admin';
import {
  Settings,
  Save,
  Loader2,
  AlertTriangle,
  Crown,
  Users,
  Lock,
  Globe,
  Image as ImageIcon,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

export default function CommunityAdminSettings() {
  const { community, userRole } = useOutletContext();
  const [settings, setSettings] = useState({
    name: '',
    description: '',
    rules: '',
    isPrivate: false,
    allowMemberPosts: true,
    requireApproval: false,
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [transferDialog, setTransferDialog] = useState({
    open: false,
    newOwnerOid: '',
  });

  const updateSettingsMutation = useUpdateCommunitySettings(community?.oid);
  const transferOwnershipMutation = useTransferOwnership(community?.oid);

  // Initialize settings from community
  useEffect(() => {
    if (community) {
      setSettings({
        name: community.name || '',
        description: community.description || '',
        rules: community.rules || '',
        isPrivate: community.isPrivate || false,
        allowMemberPosts: community.allowMemberPosts !== false,
        requireApproval: community.requireApproval || false,
      });
    }
  }, [community]);

  const handleChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    await updateSettingsMutation.mutateAsync(settings);
    setHasChanges(false);
  };

  const handleTransferOwnership = async () => {
    await transferOwnershipMutation.mutateAsync(transferDialog.newOwnerOid);
    setTransferDialog({ open: false, newOwnerOid: '' });
  };

  // Get list of admins who can receive ownership
  const admins = community?.members?.filter((m) => m.role === 'admin') || [];

  // Only owners can access full settings, admins can only view
  const canEdit = userRole === 'owner';
  const canEditPartial = userRole === 'owner' || userRole === 'admin';

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-zinc-400">Configure your community settings</p>
        </div>
        {canEditPartial && hasChanges && (
          <Button
            onClick={handleSave}
            disabled={updateSettingsMutation.isPending}
            className="bg-violet-600 hover:bg-violet-700"
          >
            {updateSettingsMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        )}
      </div>

      {/* Basic Info */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Basic Information
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Core community settings
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-zinc-300">
              Community Name
            </Label>
            <Input
              id="name"
              value={settings.name}
              onChange={(e) => handleChange('name', e.target.value)}
              disabled={!canEdit}
              className="bg-zinc-800 border-zinc-700 text-white disabled:opacity-50"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-zinc-300">
              Description
            </Label>
            <Textarea
              id="description"
              value={settings.description}
              onChange={(e) => handleChange('description', e.target.value)}
              disabled={!canEditPartial}
              rows={4}
              className="bg-zinc-800 border-zinc-700 text-white disabled:opacity-50"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="rules" className="text-zinc-300">
              Community Rules
            </Label>
            <Textarea
              id="rules"
              value={settings.rules}
              onChange={(e) => handleChange('rules', e.target.value)}
              disabled={!canEditPartial}
              rows={6}
              placeholder="Enter community rules (one per line)..."
              className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 disabled:opacity-50"
            />
          </div>
        </CardContent>
      </Card>

      {/* Privacy & Access */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Privacy & Access
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Control who can access and interact with your community
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-zinc-700 rounded-lg">
                {settings.isPrivate ? (
                  <Lock className="w-5 h-5 text-zinc-300" />
                ) : (
                  <Globe className="w-5 h-5 text-zinc-300" />
                )}
              </div>
              <div>
                <p className="font-medium text-white">Private Community</p>
                <p className="text-sm text-zinc-400">
                  {settings.isPrivate
                    ? 'Only approved members can view and join'
                    : 'Anyone can view and join this community'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.isPrivate}
              onCheckedChange={(checked) => handleChange('isPrivate', checked)}
              disabled={!canEdit}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-zinc-700 rounded-lg">
                <Users className="w-5 h-5 text-zinc-300" />
              </div>
              <div>
                <p className="font-medium text-white">Allow Member Posts</p>
                <p className="text-sm text-zinc-400">
                  {settings.allowMemberPosts
                    ? 'All members can create discussions'
                    : 'Only staff can create discussions'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.allowMemberPosts}
              onCheckedChange={(checked) =>
                handleChange('allowMemberPosts', checked)
              }
              disabled={!canEditPartial}
            />
          </div>

          <div className="flex items-center justify-between p-4 bg-zinc-800/50 rounded-lg">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-zinc-700 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-zinc-300" />
              </div>
              <div>
                <p className="font-medium text-white">Require Post Approval</p>
                <p className="text-sm text-zinc-400">
                  {settings.requireApproval
                    ? 'Posts require moderator approval before publishing'
                    : 'Posts are published immediately'}
                </p>
              </div>
            </div>
            <Switch
              checked={settings.requireApproval}
              onCheckedChange={(checked) =>
                handleChange('requireApproval', checked)
              }
              disabled={!canEditPartial}
            />
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone - Owner Only */}
      {canEdit && (
        <Card className="bg-zinc-900/50 border-red-900/50">
          <CardHeader>
            <CardTitle className="text-red-400 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Danger Zone
            </CardTitle>
            <CardDescription className="text-zinc-400">
              Irreversible and destructive actions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Transfer Ownership */}
            <div className="flex items-center justify-between p-4 bg-red-950/20 rounded-lg border border-red-900/30">
              <div>
                <p className="font-medium text-white">Transfer Ownership</p>
                <p className="text-sm text-zinc-400">
                  Transfer this community to another admin. This action cannot
                  be undone.
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() =>
                  setTransferDialog({ open: true, newOwnerOid: '' })
                }
                disabled={admins.length === 0}
                className="border-red-900/50 text-red-400 hover:bg-red-950/30 hover:text-red-300"
              >
                <Crown className="w-4 h-4 mr-2" />
                Transfer
              </Button>
            </div>
            {admins.length === 0 && (
              <p className="text-xs text-zinc-500 pl-4">
                You need to promote at least one member to admin before
                transferring ownership.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Transfer Ownership Dialog */}
      <Dialog
        open={transferDialog.open}
        onOpenChange={(open) =>
          !open && setTransferDialog({ open: false, newOwnerOid: '' })
        }
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Crown className="w-5 h-5" />
              Transfer Ownership
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This action will make the selected admin the new owner of this
              community. You will be demoted to admin. This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-zinc-300">Select New Owner</Label>
              <Select
                value={transferDialog.newOwnerOid}
                onValueChange={(value) =>
                  setTransferDialog((prev) => ({ ...prev, newOwnerOid: value }))
                }
              >
                <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Choose an admin" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {admins.map((admin) => (
                    <SelectItem
                      key={admin.userOid}
                      value={admin.userOid}
                      className="text-zinc-300 hover:text-white hover:bg-zinc-800"
                    >
                      {admin.username || admin.userOid}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() =>
                setTransferDialog({ open: false, newOwnerOid: '' })
              }
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              Cancel
            </Button>
            <Button
              onClick={handleTransferOwnership}
              disabled={
                !transferDialog.newOwnerOid ||
                transferOwnershipMutation.isPending
              }
              className="bg-red-600 hover:bg-red-700"
            >
              {transferOwnershipMutation.isPending && (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              )}
              Transfer Ownership
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
